import type { AgeGroup } from "@/api/hooks/campaignForm"
import { ALL_AGES } from "../state/formReducer"

/**
 * Age group chip buttons. Parity: `adgroup.js:160-185`.
 *
 *   - Chips are derived from the `/age-groups` response with an
 *     explicit "All Ages" entry prepended.
 *   - Clicking "All Ages" is exclusive: it clears every other chip
 *     (handled in the reducer — we just dispatch the toggle here).
 *   - Chip labels strip the trailing "years old" suffix from the
 *     backend name (e.g. "13-17 years old" → "13-17").
 */

interface Props {
  ages: AgeGroup[] | undefined
  selected: string[]
  onToggle: (id: string) => void
  hiddenIds?: string[]
}

export function AgeGroupChips({ ages, selected, onToggle, hiddenIds = [] }: Props) {
  const hidden = new Set(hiddenIds)
  // Dedup + always include ALL_AGES at the top.
  const options = (() => {
    const out: Array<{ id: string; name: string }> = [
      { id: ALL_AGES, name: "All Ages" },
    ]
    const seen = new Set<string>([ALL_AGES])
    for (const age of ages ?? []) {
      const id = age.id || age.value || ""
      const name = age.name || age.label || id
      if (!id || seen.has(id)) continue
      if (hidden.has(id)) continue
      if (id === ALL_AGES) continue
      if (String(name).trim().toLowerCase() === "all ages") continue
      seen.add(id)
      out.push({ id, name })
    }
    return out
  })()

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = selected.includes(opt.id)
        const label = String(opt.name)
          .replace(/\s*years?\s*old\s*/i, "")
          .trim()
        return (
          <button
            type="button"
            key={opt.id}
            onClick={() => onToggle(opt.id)}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${
              active
                ? "border-brand-accent bg-brand-accent/20 text-brand-fg"
                : "border-brand-border bg-brand-bg/40 text-white hover:bg-brand-border/30"
            }`}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}
